import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, LayoutGrid } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ResponsivePreviewSection from "@/components/ResponsivePreviewSection";
import ProcessSection from "@/components/sections/ProcessSection";
import { getPortfolioDemo } from "@/pages/portfolio/demos";
import { usePortfolioProject } from "@/lib/portfolioCms";
import {
  getGalleryDefault,
  isGalleryVariant,
} from "@/data/portfolioGalleryRegistry";
import {
  BRAND_WEB_VARIANTS,
  DEFAULT_BRAND_WEB_VARIANT,
} from "@/pages/portfolio/demos/brandWebVariants";
import CategoryPortfolioGallery from "@/pages/portfolio/demos/shared/CategoryPortfolioGallery";
import { getIndustryDemo } from "@/pages/portfolio/demos/shared/industryCatalog";
import PortfolioDetailScrollSections from "@/pages/portfolio/PortfolioDetailScrollSections";
import PortfolioDetailIntroGallery from "@/pages/portfolio/PortfolioDetailIntroGallery";
import { openContactForm } from "@/lib/contactForm";
import { CapabilitiesReturnLink } from "@/pages/search/KnowledgeChrome";
import "./PortfolioDetailPage.css";

function PortfolioMockup({
  project,
  variant,
  onVariantChange,
  demoId,
  onDemoChange,
}) {
  const Demo = getPortfolioDemo(project.slug);
  const usesGallery = project.slug === "brand-web" && isGalleryVariant(variant);
  const [galleryActive, setGalleryActive] = useState(
    getGalleryDefault(variant) ?? getGalleryDefault(DEFAULT_BRAND_WEB_VARIANT),
  );

  useEffect(() => {
    if (isGalleryVariant(variant)) {
      setGalleryActive(getGalleryDefault(variant));
    }
  }, [variant]);

  const embedKey =
    project.slug === "brand-web"
      ? usesGallery
        ? `${project.slug}-${variant}`
        : `${project.slug}-${variant}${demoId ? `-${demoId}` : ""}`
      : project.slug;
  const activeVariant = BRAND_WEB_VARIANTS.find((v) => v.id === variant) ?? BRAND_WEB_VARIANTS[0];
  const activeDemo = demoId ? getIndustryDemo(variant, demoId) : null;
  const mockupUrl = usesGallery
    ? galleryActive?.url ?? activeVariant.url
    : project.slug === "brand-web"
      ? activeDemo?.url ?? activeVariant.url
      : project.url;

  return (
    <div className="ds-portfolio-detail__mockup-wrap">
      {project.slug === "brand-web" && (
        <div className="ds-portfolio-detail__variants" data-testid="portfolio-detail-variants">
          {BRAND_WEB_VARIANTS.map((v) => (
            <button
              key={v.id}
              type="button"
              className={`ds-portfolio-detail__variant-btn${variant === v.id ? " is-active" : ""}`}
              style={{ "--v-accent": v.accent }}
              onClick={() => onVariantChange?.(v.id)}
            >
              {v.label}
            </button>
          ))}
        </div>
      )}
      <div className="ds-portfolio-detail__mockup" data-testid="portfolio-detail-mockup">
        <div className="ds-portfolio-detail__mockup-bar">
          <span className="ds-portfolio-detail__mockup-dot ds-portfolio-detail__mockup-dot--r" />
          <span className="ds-portfolio-detail__mockup-dot ds-portfolio-detail__mockup-dot--y" />
          <span className="ds-portfolio-detail__mockup-dot ds-portfolio-detail__mockup-dot--g" />
          <div className="ds-portfolio-detail__mockup-url">{mockupUrl}</div>
          <span className="ds-portfolio-detail__tag ds-portfolio-detail__tag--accent">{project.tag}</span>
        </div>
        <div
          className={`ds-portfolio-detail__mockup-frame${
            project.slug === "brand-web" ? " ds-portfolio-detail__mockup-frame--tall" : ""
          }${usesGallery ? " ds-portfolio-detail__mockup-frame--gallery" : ""}`}
        >
          <div className="ds-portfolio-detail__mockup-inline" key={embedKey}>
            {usesGallery ? (
              <CategoryPortfolioGallery
                categoryId={variant}
                embed
                onActiveChange={setGalleryActive}
              />
            ) : Demo ? (
              <Demo
                accent={project.accent}
                embed
                variant={variant}
                demo={demoId}
                onDemoChange={onDemoChange}
              />
            ) : (
              <p className="ds-portfolio-detail__mockup-fallback">미리보기를 불러올 수 없습니다.</p>
            )}
          </div>
        </div>
      </div>
      <p className="ds-portfolio-detail__mockup-hint ds-portfolio-detail__mockup-hint--solo">
        {usesGallery
          ? "업종별 실제 제작 사례 · 미리보기에서 스크롤하여 전체 화면을 확인하세요"
          : project.slug === "brand-web"
            ? "업종별 제작 결과물 · 미리보기에서 전체를 확인하세요"
            : "제작 결과물 · 미리보기에서 전체를 확인하세요"}
      </p>
    </div>
  );
}

export default function PortfolioDetailPage({ previewProject = null }) {
  const { slug: routeSlug } = useParams();
  const slug = previewProject?.slug || routeSlug;
  const navigate = useNavigate();
  const location = useLocation();
  const fetched = usePortfolioProject(previewProject ? null : slug);
  const project = previewProject || fetched.project;
  const loading = previewProject ? false : fetched.loading;
  const [brandVariant, setBrandVariant] = useState(DEFAULT_BRAND_WEB_VARIANT);
  const [demoId, setDemoId] = useState(null);

  const goToContact = () => {
    openContactForm();
  };

  const goBackToPortfolio = () => {
    navigate({ pathname: "/", hash: "portfolio" });
  };

  const handleBrandVariantChange = (next) => {
    setBrandVariant(next);
    setDemoId(null);
  };

  useEffect(() => {
    if (!location.hash) return undefined;

    const id = location.hash.slice(1);
    let cancelled = false;
    let attempts = 0;

    const scrollToHash = () => {
      if (cancelled) return;
      const target = document.getElementById(id);
      if (target) {
        target.scrollIntoView({ behavior: "smooth" });
        return;
      }
      if (attempts < 24) {
        attempts += 1;
        requestAnimationFrame(scrollToHash);
      }
    };

    requestAnimationFrame(scrollToHash);
    return () => {
      cancelled = true;
    };
  }, [location.hash, slug]);

  if (loading) {
    return (
      <div className="ds-portfolio-detail">
        <Header />
        <div className="ds-portfolio-detail__inner ds-portfolio-detail--missing">
          <p>불러오는 중...</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="ds-portfolio-detail">
        <Header />
        <div className="ds-portfolio-detail__inner ds-portfolio-detail--missing">
          <p>요청하신 포트폴리오를 찾을 수 없습니다.</p>
          <button type="button" onClick={goBackToPortfolio} className="ds-portfolio-detail__back">
            <ArrowLeft size={16} />
            Back to Portfolio
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div
      className="ds-portfolio-detail"
      data-testid="portfolio-detail-page"
      style={{ "--pf-accent": project.accent }}
    >
      <div className="ds-portfolio-detail__grid" aria-hidden="true" />
      <Header />
      <div className="ds-portfolio-detail__intro-wrap">
        <div className="ds-portfolio-detail__intro" data-testid="portfolio-intro-scene">
          <PortfolioDetailIntroGallery />
        </div>
      </div>
      <main className="ds-portfolio-detail__inner">
        <CapabilitiesReturnLink />
        <button
          type="button"
          onClick={goToContact}
          className="ds-portfolio-detail__back"
          data-testid="portfolio-detail-contact-cta"
        >
          문의하기
        </button>

        <section className="ds-portfolio-detail__hero" aria-labelledby="portfolio-detail-title">
          <div className="ds-portfolio-detail__tags">
            <span className="ds-portfolio-detail__tag ds-portfolio-detail__tag--accent">{project.tag}</span>
            {(project.categoryTags || []).map((label) => (
              <span key={label} className="ds-portfolio-detail__tag">
                {label}
              </span>
            ))}
          </div>

          <header className="ds-portfolio-detail__hero-head">
            <h1 className="ds-portfolio-detail__title" id="portfolio-detail-title" data-testid="portfolio-detail-title">
              {project.title}
            </h1>
          </header>

          <div className="ds-portfolio-detail__hero-grid">
            <p className="ds-portfolio-detail__lead">{project.shortDescription}</p>
            <aside className="ds-portfolio-detail__device-preview" data-testid="portfolio-device-preview">
              <ResponsivePreviewSection variant="portfolio-detail" />
            </aside>
          </div>
        </section>

        <PortfolioMockup
          project={project}
          variant={slug === "brand-web" ? brandVariant : undefined}
          onVariantChange={slug === "brand-web" ? handleBrandVariantChange : undefined}
          demoId={slug === "brand-web" ? demoId : undefined}
          onDemoChange={slug === "brand-web" ? setDemoId : undefined}
        />

        <div className="ds-portfolio-detail__sections-divider" aria-hidden="true" />

        <div className="ds-portfolio-detail__process">
          <ProcessSection embed />
        </div>

        <PortfolioDetailScrollSections project={project} />

        <div className="ds-portfolio-detail__footer-cta">
          <button
            type="button"
            onClick={goBackToPortfolio}
            className="ds-portfolio-detail__cta-btn ds-portfolio-detail__cta-btn--accent"
            data-testid="portfolio-detail-back"
          >
            <LayoutGrid size={18} aria-hidden="true" />
            Back to Portfolio
          </button>
        </div>
      </main>
      <Footer />
    </div>
  );
}
